/**
 * Procedural venetian-blind gobo: a canvas-generated horizontal stripe
 * texture projected by the office key spotlight (SpotLight.map).
 * Owned by LIGHT; see CONTRACTS.md.
 */
import { CanvasTexture, SRGBColorSpace } from 'three'
import {
  GOBO_EDGE_SOFTNESS_PX,
  GOBO_OPEN_FRACTION,
  GOBO_SIZE_PX,
  GOBO_SLAT_COUNT
} from './constants'

const BLOCKED_STYLE = '#000000'
const OPEN_STYLE = '#ffffff'

export const createVenetianGoboTexture = (): CanvasTexture => {
  const canvas = document.createElement('canvas')
  canvas.width = GOBO_SIZE_PX
  canvas.height = GOBO_SIZE_PX
  const context = canvas.getContext('2d')
  if (!context) {
    throw new Error('GUMSHOE lighting: 2D canvas context unavailable for gobo')
  }

  context.fillStyle = BLOCKED_STYLE
  context.fillRect(0, 0, GOBO_SIZE_PX, GOBO_SIZE_PX)

  const period = GOBO_SIZE_PX / GOBO_SLAT_COUNT
  const openHeight = period * GOBO_OPEN_FRACTION

  for (let slat = 0; slat < GOBO_SLAT_COUNT; slat += 1) {
    const top = slat * period + (period - openHeight) / 2
    const gradient = context.createLinearGradient(0, top, 0, top + openHeight)
    const soft = Math.min(GOBO_EDGE_SOFTNESS_PX / openHeight, 0.45)
    gradient.addColorStop(0, BLOCKED_STYLE)
    gradient.addColorStop(soft, OPEN_STYLE)
    gradient.addColorStop(1 - soft, OPEN_STYLE)
    gradient.addColorStop(1, BLOCKED_STYLE)
    context.fillStyle = gradient
    context.fillRect(0, top, GOBO_SIZE_PX, openHeight)
  }

  const texture = new CanvasTexture(canvas)
  texture.colorSpace = SRGBColorSpace
  texture.needsUpdate = true
  return texture
}
